import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Plus, AlertTriangle, CheckCircle2, Clock, MessageSquare,
  Loader2, Send, Trash2, Check, X, ChevronDown, ChevronUp, User, Camera,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  ocorrenciasService,
  Ocorrencia,
  OcorrenciaComment,
  OcorrenciaPerson,
  OcorrenciaUnit,
} from '../../../services/ocorrenciasService';
import { makeAbsolutePhotoUrl, fetchWithAuth, getApiBaseUrl } from '../../../services/api';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import toast from 'react-hot-toast';
import PhotoActionModal from '../../PhotoActionModal';
import PhotoUploadModal, { PhotoResult } from '../../PhotoUploadModal';
import PhotoWebcamModal from '../../PhotoWebcamModal';
import { UnitSelector, UnitOption } from '../../UnitSelector';
import { CondoType, formatUnitLabel } from '../../../utils/terminology';

interface OcorrenciasPanelProps {
  customerId: string;
  onBack?: () => void;
  lightTheme?: boolean;
  /** Show the Unit/Morador picker on the create form (Operational View only — residents self-reporting don't need it). */
  allowTargeting?: boolean;
  condoType?: CondoType;
  /** Navigate to the unit's location page — when omitted, the unit target label isn't clickable. */
  onOpenUnit?: (unitId: string, unitLabel: string, blockId?: string) => void;
}

interface OcPersonOption { id: string; first_name: string; last_name: string; }

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// "Quadra 23 – Lote 18" / "Bloco A – Unidade 101" / "Rua das Flores, 45 – Lote 18"
function unitOnlyLabel(unit: OcorrenciaUnit, condoType?: CondoType): string {
  return formatUnitLabel(
    { number: unit.number, blockName: unit.block?.name, streetName: unit.street?.name, streetNumber: unit.street_number },
    condoType
  );
}

// unitOnlyLabel(...) + " — João Silva" when a subject resident was picked
function unitTargetLabel(oc: Ocorrencia, condoType?: CondoType): string | null {
  if (!oc.unit) return null;
  const unitPart = unitOnlyLabel(oc.unit, condoType);
  const subject = oc.subject_person ? `${oc.subject_person.first_name} ${oc.subject_person.last_name}` : null;
  return subject ? `${unitPart} — ${subject}` : unitPart;
}

// Reporter display name — falls back to the cached Keycloak identity (created_by_name)
// when the reporter had no `people` row for this customer (staff/admin fallback).
function reporterName(oc: Ocorrencia): string {
  if (oc.person) return `${oc.person.first_name} ${oc.person.last_name}`;
  return oc.created_by_name || 'Usuário';
}

function PersonAvatar({ person, fallbackName, size = 32 }: { person?: OcorrenciaPerson; fallbackName?: string; size?: number }) {
  const photoUrl = person?.identity_photo_url || person?.profile_picture_url;
  const absoluteUrl = photoUrl ? makeAbsolutePhotoUrl(photoUrl) : null;
  const initials = person
    ? `${(person.first_name || '')[0] || ''}${(person.last_name || '')[0] || ''}`.toUpperCase()
    : (fallbackName || '?')[0]?.toUpperCase() || '?';

  if (absoluteUrl) {
    return (
      <img
        src={absoluteUrl}
        alt={initials}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-blue-600 flex items-center justify-center shrink-0 text-white font-medium"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials || <User size={size * 0.5} />}
    </div>
  );
}

const OcorrenciasPanel: React.FC<OcorrenciasPanelProps> = ({ customerId, onBack, lightTheme = false, allowTargeting = false, condoType, onOpenUnit }) => {
  const { hasAnyRole } = useKeycloak();
  const isAdmin = hasAnyRole('admin', 'supervisor', 'sindico', 'super_admin');

  const [ocorrencias, setOcorrencias] = useState<Ocorrencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('all');

  // Selected ocorrencia detail
  const [selectedOc, setSelectedOc] = useState<Ocorrencia | null>(null);
  const [comments, setComments] = useState<OcorrenciaComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Photo attachment for the entry being drafted
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoActionModal, setShowPhotoActionModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoWebcamModal, setShowPhotoWebcamModal] = useState(false);

  const handlePhotoResult = async (result: PhotoResult) => {
    setPhotoPreview(result.base64);
    setUploadingPhoto(true);
    try {
      const file = new File([result.blob], 'ocorrencia.jpg', { type: 'image/jpeg' });
      const res = await ocorrenciasService.uploadPhoto(customerId, file);
      setPhotoUrl(res.photo_url);
    } catch {
      toast.error('Erro ao enviar foto');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Unit/Morador targeting (Operational View only — see allowTargeting)
  const [selectedUnitOption, setSelectedUnitOption] = useState<UnitOption | null>(null);
  const [personOptions, setPersonOptions] = useState<OcPersonOption[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [loadingPersons, setLoadingPersons] = useState(false);

  // Actions
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadOcorrencias = useCallback(async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const data = await ocorrenciasService.listOcorrencias(customerId, status);
      setOcorrencias(data);
    } catch {
      toast.error('Erro ao carregar ocorrências');
    } finally {
      setLoading(false);
    }
  }, [customerId, statusFilter]);

  useEffect(() => {
    loadOcorrencias();
  }, [loadOcorrencias]);

  // Load residents when a unit is selected
  useEffect(() => {
    setSelectedPersonId('');
    const unitId = selectedUnitOption?.value;
    if (!unitId || !customerId) {
      setPersonOptions([]);
      return;
    }
    setLoadingPersons(true);
    const params = new URLSearchParams({ page_size: '50', role_type: 'resident', unit_id: unitId });
    fetchWithAuth(`${getApiBaseUrl()}/api/v1/customers/${customerId}/people?${params}`)
      .then(async r => {
        if (!r.ok) return;
        const data = await r.json();
        setPersonOptions(data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingPersons(false));
  }, [selectedUnitOption, customerId]);

  const resetTargeting = useCallback(() => {
    setSelectedUnitOption(null);
    setPersonOptions([]);
    setSelectedPersonId('');
  }, []);

  const closeCreateForm = useCallback(() => {
    setShowCreateForm(false);
    setCreateTitle('');
    setCreateDesc('');
    setPhotoPreview(null);
    setPhotoUrl(null);
    resetTargeting();
  }, [resetTargeting]);

  const loadComments = useCallback(async (ocId: string) => {
    setLoadingComments(true);
    try {
      const data = await ocorrenciasService.listComments(customerId, ocId);
      setComments(data);
    } catch {
      toast.error('Erro ao carregar comentários');
    } finally {
      setLoadingComments(false);
    }
  }, [customerId]);

  const handleSelectOc = useCallback(async (oc: Ocorrencia) => {
    setSelectedOc(oc);
    setCommentText('');
    await loadComments(oc.id);
  }, [loadComments]);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const handleCreate = async () => {
    if (!createTitle.trim() || !createDesc.trim()) {
      toast.error('Título e descrição são obrigatórios');
      return;
    }
    if (allowTargeting && !selectedUnitOption) {
      toast.error('Selecione a unidade');
      return;
    }
    if (uploadingPhoto) return;
    setCreating(true);
    try {
      const created = await ocorrenciasService.createOcorrencia(customerId, {
        title: createTitle.trim(),
        description: createDesc.trim(),
        photo_url: photoUrl || undefined,
        unit_id: selectedUnitOption?.value || undefined,
        subject_person_id: selectedPersonId || undefined,
      });
      toast.success('Ocorrência registrada');
      closeCreateForm();
      loadOcorrencias();
      handleSelectOc(created);
    } catch {
      toast.error('Erro ao registrar ocorrência');
    } finally {
      setCreating(false);
    }
  };

  const handleResolve = async (ocId: string) => {
    setResolvingId(ocId);
    try {
      await ocorrenciasService.resolveOcorrencia(customerId, ocId);
      toast.success('Ocorrência resolvida');
      loadOcorrencias();
      if (selectedOc?.id === ocId) {
        setSelectedOc(prev => prev ? { ...prev, status: 'resolved' } : prev);
      }
    } catch {
      toast.error('Erro ao resolver ocorrência');
    } finally {
      setResolvingId(null);
    }
  };

  const handleDelete = async (ocId: string) => {
    setDeletingId(ocId);
    try {
      await ocorrenciasService.deleteOcorrencia(customerId, ocId);
      toast.success('Ocorrência removida');
      if (selectedOc?.id === ocId) setSelectedOc(null);
      loadOcorrencias();
    } catch {
      toast.error('Erro ao remover ocorrência');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendComment = async () => {
    if (!selectedOc || !commentText.trim()) return;
    setSendingComment(true);
    try {
      const comment = await ocorrenciasService.addComment(customerId, selectedOc.id, { content: commentText.trim() });
      setComments(prev => [...prev, comment]);
      setCommentText('');
    } catch {
      toast.error('Erro ao enviar comentário');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedOc) return;
    try {
      await ocorrenciasService.deleteComment(customerId, selectedOc.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      toast.error('Erro ao remover comentário');
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const border = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-gray-100';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const cardBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-700';
  const inputClass = cn(
    'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
    lightTheme
      ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
      : 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
  );

  const statusBadge = (status: string) => {
    if (status === 'open') {
      return lightTheme
        ? 'bg-amber-100 text-amber-700 border border-amber-300'
        : 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
    }
    return lightTheme
      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
      : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
  };

  // ─── Detail View ──────────────────────────────────────────────────────────
  if (selectedOc) {
    return (
      <div className={cn('flex flex-col h-full', bg)}>
        {/* Header */}
        <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
          <button
            onClick={() => setSelectedOc(null)}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className={cn('font-semibold text-sm truncate', text)}>{selectedOc.title}</p>
          </div>
          <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', statusBadge(selectedOc.status))}>
            {selectedOc.status === 'open' ? 'Aberta' : 'Resolvida'}
          </span>
          {isAdmin && selectedOc.status === 'open' && (
            <button
              onClick={() => handleResolve(selectedOc.id)}
              disabled={resolvingId === selectedOc.id}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs rounded-lg transition-colors"
            >
              {resolvingId === selectedOc.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Resolver
            </button>
          )}
          {(isAdmin) && (
            <button
              onClick={() => handleDelete(selectedOc.id)}
              disabled={deletingId === selectedOc.id}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-500 hover:text-red-400')}
            >
              {deletingId === selectedOc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
          )}
        </div>

        {/* Ocorrência body */}
        <div className={cn('px-4 py-4 border-b shrink-0', border)}>
          <div className="flex items-center gap-2 mb-2">
            <PersonAvatar person={selectedOc.person} fallbackName={selectedOc.created_by_name} size={28} />
            <span className={cn('text-sm font-medium', text)}>
              {reporterName(selectedOc)}
            </span>
            <span className={cn('text-xs ml-auto', subtext)}>{formatDate(selectedOc.created_at)}</span>
          </div>
          {selectedOc.unit && (
            <button
              type="button"
              onClick={() => onOpenUnit?.(selectedOc.unit!.id, unitOnlyLabel(selectedOc.unit!, condoType), selectedOc.unit!.block?.id)}
              disabled={!onOpenUnit}
              className={cn(
                'text-xs font-medium mb-2 text-left',
                lightTheme ? 'text-blue-600' : 'text-blue-400',
                onOpenUnit && 'hover:underline'
              )}
            >
              {unitTargetLabel(selectedOc, condoType)}
            </button>
          )}
          {selectedOc.photo_url && (
            <img
              src={makeAbsolutePhotoUrl(selectedOc.photo_url)}
              alt="foto"
              className="w-24 h-24 object-cover rounded-lg border border-gray-700/30 mb-2 cursor-pointer"
              onClick={() => window.open(makeAbsolutePhotoUrl(selectedOc.photo_url!), '_blank')}
            />
          )}
          <div className="max-h-32 overflow-y-auto">
            <p className={cn('text-sm whitespace-pre-wrap leading-relaxed', subtext)}>{selectedOc.description}</p>
          </div>
        </div>

        {/* Comments */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 space-y-3">
          <p className={cn('text-xs font-medium uppercase tracking-wider', subtext)}>
            Comentários ({comments.length})
          </p>
          {loadingComments ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className={cn('animate-spin', subtext)} />
            </div>
          ) : comments.length === 0 ? (
            <p className={cn('text-sm text-center py-6', subtext)}>Nenhum comentário ainda. Seja o primeiro a comentar.</p>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className={cn('flex gap-2.5 group')}>
                <PersonAvatar person={comment.person} fallbackName={comment.created_by_name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className={cn('text-sm font-medium', text)}>
                      {comment.person ? `${comment.person.first_name} ${comment.person.last_name}` : (comment.created_by_name || 'Usuário')}
                    </span>
                    <span className={cn('text-xs', subtext)}>{formatDate(comment.created_at)}</span>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className={cn(
                        'ml-auto opacity-0 group-hover:opacity-100 p-1 rounded transition-all',
                        lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-600 hover:text-red-400'
                      )}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                  <p className={cn('text-sm whitespace-pre-wrap', subtext)}>{comment.content}</p>
                </div>
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>

        {/* Comment input */}
        <div className={cn('px-4 py-3 border-t shrink-0 flex gap-2', border)}>
          <input
            className={cn(inputClass, 'flex-1')}
            placeholder="Escreva um comentário..."
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment(); } }}
          />
          <button
            onClick={handleSendComment}
            disabled={sendingComment || !commentText.trim()}
            className="p-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg transition-colors"
          >
            {sendingComment ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────────
  return (
    <div className={cn('flex flex-col h-full', bg)}>
      {/* Header */}
      <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
        {onBack && (
          <button
            onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <h2 className={cn('font-semibold text-base uppercase tracking-wide', text)}>Ocorrências</h2>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
        >
          <Plus size={14} />
          Nova
        </button>
      </div>

      {/* Filter tabs */}
      <div className={cn('flex gap-1 px-4 py-2 border-b shrink-0', border)}>
        {(['all', 'open', 'resolved'] as const).map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={cn(
              'px-3 py-1 text-xs rounded-full font-medium transition-colors',
              statusFilter === f
                ? 'bg-blue-600 text-white'
                : lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            )}
          >
            {f === 'all' ? 'Todas' : f === 'open' ? 'Abertas' : 'Resolvidas'}
          </button>
        ))}
      </div>

      {/* New ocorrência — popup modal, matching the other registration modals (EncomendaFormModal, VehicleFormModal) */}
      {showCreateForm && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => { if (!creating && !uploadingPhoto) closeCreateForm(); }} />
          <div className={cn('relative w-full max-w-md rounded-xl shadow-xl p-5 mx-4 border', bg, border)}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className={cn('h-5 w-5', subtext)} />
                <h2 className={cn('font-semibold text-sm', text)}>Nova Ocorrência</h2>
              </div>
              <button
                type="button"
                onClick={closeCreateForm}
                disabled={creating || uploadingPhoto}
                className={cn('p-1.5 rounded transition-colors disabled:opacity-50', lightTheme ? 'text-gray-400 hover:text-gray-700' : 'text-gray-500 hover:text-gray-300')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                className={inputClass}
                placeholder="Título da ocorrência"
                value={createTitle}
                onChange={e => setCreateTitle(e.target.value)}
                maxLength={200}
                autoFocus
              />
              {allowTargeting && (
                <div className="space-y-2">
                  <UnitSelector
                    customerId={customerId}
                    value={selectedUnitOption}
                    onChange={setSelectedUnitOption}
                    condoType={condoType}
                    darkMode={!lightTheme}
                  />
                  <select
                    className={inputClass}
                    value={selectedPersonId}
                    onChange={e => setSelectedPersonId(e.target.value)}
                    disabled={!selectedUnitOption || loadingPersons}
                  >
                    <option value="">{loadingPersons ? 'Carregando...' : 'Morador (opcional)'}</option>
                    {personOptions.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                  </select>
                </div>
              )}
              <textarea
                className={cn(inputClass, 'h-24 resize-none')}
                placeholder="Descreva a ocorrência em detalhes..."
                value={createDesc}
                onChange={e => setCreateDesc(e.target.value)}
              />
              <div className="flex items-center gap-2">
                {photoPreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-700/50 w-16 h-16 shrink-0">
                    <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                    {uploadingPhoto && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { setPhotoPreview(null); setPhotoUrl(null); }}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPhotoActionModal(true)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                      lightTheme ? 'bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-300' : 'bg-gray-800 hover:bg-gray-700 text-gray-400 border-gray-600'
                    )}
                  >
                    <Camera size={14} /> Adicionar foto
                  </button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <button
                type="button"
                onClick={closeCreateForm}
                disabled={creating || uploadingPhoto}
                className={cn('flex-1 py-2 rounded text-sm font-semibold border transition-colors disabled:opacity-50', lightTheme ? 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700')}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || uploadingPhoto}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white transition-colors"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Registrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className={cn('animate-spin', subtext)} />
          </div>
        ) : ocorrencias.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-3">
            <AlertTriangle size={40} className={subtext} />
            <p className={cn('text-sm', subtext)}>Nenhuma ocorrência encontrada</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="text-sm text-blue-500 hover:text-blue-400 transition-colors"
            >
              Registrar ocorrência
            </button>
          </div>
        ) : (
          ocorrencias.map(oc => {
            const commentCount = oc.comments?.length ?? 0;
            return (
              <button
                key={oc.id}
                onClick={() => handleSelectOc(oc)}
                className={cn(
                  'w-full text-left rounded-xl border p-3 transition-colors hover:border-blue-500/50',
                  cardBg
                )}
              >
                <div className="flex items-start gap-2">
                  <PersonAvatar person={oc.person} fallbackName={oc.created_by_name} size={28} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={cn('text-sm font-medium truncate flex-1', text)}>{oc.title}</span>
                      <span className={cn('text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0', statusBadge(oc.status))}>
                        {oc.status === 'open' ? 'Aberta' : 'Resolvida'}
                      </span>
                    </div>
                    <p className={cn('text-xs truncate mb-1', subtext)}>{oc.description}</p>
                    {oc.unit && (
                      <span
                        role={onOpenUnit ? 'button' : undefined}
                        tabIndex={onOpenUnit ? 0 : undefined}
                        onClick={onOpenUnit ? (e) => { e.stopPropagation(); onOpenUnit(oc.unit!.id, unitOnlyLabel(oc.unit!, condoType), oc.unit!.block?.id); } : undefined}
                        onKeyDown={onOpenUnit ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpenUnit(oc.unit!.id, unitOnlyLabel(oc.unit!, condoType), oc.unit!.block?.id); } } : undefined}
                        className={cn(
                          'block text-xs font-medium truncate mb-1',
                          lightTheme ? 'text-blue-600' : 'text-blue-400',
                          onOpenUnit && 'hover:underline'
                        )}
                      >
                        {unitTargetLabel(oc, condoType)}
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <span className={cn('text-xs', subtext)}>
                        {reporterName(oc)}
                      </span>
                      <span className={cn('text-xs', subtext)}>{formatDate(oc.created_at)}</span>
                      {commentCount > 0 && (
                        <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                          <MessageSquare size={11} />
                          {commentCount}
                        </span>
                      )}
                      {oc.photo_url && (
                        <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                          <Camera size={11} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <PhotoActionModal
        open={showPhotoActionModal}
        onClose={() => setShowPhotoActionModal(false)}
        onUpload={() => { setShowPhotoActionModal(false); setShowPhotoUploadModal(true); }}
        onWebcam={() => { setShowPhotoActionModal(false); setShowPhotoWebcamModal(true); }}
        onIPCamera={() => {}}
        title="Foto da Ocorrência"
        darkMode={!lightTheme}
        hideIPCamera
      />
      <PhotoUploadModal
        open={showPhotoUploadModal}
        onClose={() => setShowPhotoUploadModal(false)}
        title="Foto da Ocorrência"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoUploadModal(false); await handlePhotoResult(result); }}
      />
      <PhotoWebcamModal
        open={showPhotoWebcamModal}
        onClose={() => setShowPhotoWebcamModal(false)}
        title="Foto da Ocorrência"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode={!lightTheme}
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoWebcamModal(false); await handlePhotoResult(result); }}
      />
    </div>
  );
};

export default OcorrenciasPanel;
